import { z } from 'zod';
import { RecordAccount, Timestamp, Uuid } from './common.js';
import { DecisionSummary } from './decisions.js';
import { PROSE_STATEMENT_MAX_LENGTH } from './inputs.js';
import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_TOO_LONG,
  KnowledgeKind,
  ProvenanceEntry,
} from './knowledge.js';
import { RunSummary } from './runs.js';
import { InvestmentRole, OrgRelation } from './vocab.js';

/**
 * **Organisations as subjects** — `/orgs/:orgId/organisations`
 * (organisations-as-subjects note §1 R1/R8, §2 chunk 2a).
 *
 * An organisation the office tracks gets the same vertical-agnostic
 * `subject_record` row a person gets, and **no status**: a status is what this
 * org's vertical calls its relationship to a *person*, and neither vocabulary
 * has a word for an organisation (R1). Until one does, "tracked" is the whole of
 * what the marker says, and the list has no filter beyond the name search.
 *
 * **Why a sibling surface rather than `POST /subjects` with a `kind`.**
 * `CreateSubjectResponse` *is* `SubjectRecord` — a status, an identity block, the
 * held-claim tiers, the brief pin — none of which an organisation has. Widening
 * that response with a kind branch would make every field of it conditional on a
 * value the reader has to check first. So the doors live here, and the reads an
 * organisation genuinely shares with a person (the network, the Data tab, the run
 * history, the decision log) stay exactly where they are, reached by widening
 * `loadSubject`'s kinds rather than by copying a route.
 *
 * {@link OrganisationRecord} is what the page reads, and it is deliberately not
 * `SubjectRecord` with the person parts nulled out: an organisation has no
 * status, no identity block, no held-claim tiers and no brief pin, and every one
 * of those would have arrived here as a key a reader has to know to ignore.
 */

/* ————————————————————————— create ————————————————————————— */

/**
 * `POST /orgs/:orgId/organisations` — **New Organisation** (R5's second door).
 *
 * `name` carries the rename door's ceiling rather than the person create door's
 * absence of one. That is deliberate and it is the easy direction: this door is
 * new, so a maximum here can refuse nothing prod already holds.
 *
 * **No `status` and no prose requirement.** A person entered from nothing is
 * entered with a sentence about why, because the intake extraction over it is the
 * only thing seeding the record. An organisation is not: the office may know
 * nothing but a name and a website, and chunk 3's lookup is what fills the rest
 * in. So `prose` is nullable at every path through this door, and a create that
 * carries none files none.
 *
 * `website` and `prose` go through **the same code path Add what you know
 * takes** ({@link OrganisationStatementBody}), and the create's transaction is
 * where it runs: the website's host becomes a confirmed `domain` anchor through
 * an `office_statement` decision, and the prose is filed and read for the
 * relations it names. A website this build cannot read a host out of refuses the
 * whole create with `WEBSITE_UNREADABLE`, rather than leaving a half-made
 * organisation whose website did not stick.
 *
 * **`website` is not `z.string().url()`**, and that is the same reading the
 * statement door makes for the same reason: a URL check refuses `medici.org`,
 * which is what somebody types, and accepts `https://example`, which names no
 * host. Two doors onto one field cannot disagree about what a website is.
 *
 * `entity_id` and `force_new` are the person door's two answers to
 * `NAME_CONFLICT`, meaning exactly what they mean there — the same organisation
 * already on file as an untracked node (adopt it, mint nothing), or a different
 * organisation that shares the name (mint anyway). Mutually exclusive, and
 * neither is needed when nothing collides.
 */
export const CreateOrganisationBody = z
  .object({
    name: z.string().trim().min(1).max(DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_TOO_LONG),
    /** The organisation's own site. Its host becomes a confirmed `domain` anchor. */
    website: z.string().trim().nullable(),
    /**
     * What the office knows, in their own words. Filed as a note on the record.
     *
     * Bounded by {@link PROSE_STATEMENT_MAX_LENGTH} and not by the note cap: a
     * statement is read inside this request and is never split, so its length is
     * what one reading has to survive (split-giant-note note, 2026-09-14). The
     * emptiness rule is unchanged and is where it always was — blank prose is
     * not an error here, it is a create with nothing said.
     */
    prose: z.string().trim().max(PROSE_STATEMENT_MAX_LENGTH).nullable(),
    /** Track this live organisation entity instead of minting one. */
    entity_id: Uuid.optional(),
    /** Mint a new organisation even though a live namesake exists. */
    force_new: z.literal(true).optional(),
    /**
     * **The protection account this organisation belongs to** — required in a
     * security org, refused in every other. See `CreateSubjectBody.account_id`
     * for why it is optional on the wire and vetted by the server.
     */
    account_id: Uuid.optional(),
  })
  .strict()
  .refine((body) => !(body.entity_id !== undefined && body.force_new), {
    message: 'entity_id and force_new are mutually exclusive',
    path: ['force_new'],
  });
export type CreateOrganisationBody = z.infer<typeof CreateOrganisationBody>;

/* ————————————————————————— the list ————————————————————————— */

/**
 * **One tracked organisation**, and the whole of what this pass says about one.
 *
 * `id` is the entity id, which is also the `subject_record`'s primary key — one
 * row's worth of the same identity, exactly as the People list's tracked rows
 * carry it. It is never shown; it is what the record route is addressed by.
 *
 * `connections` is `entity.connection_count`, the same number the knowledge base
 * shows: how many distinct active edge-bearing fact lineages touch this
 * organisation. It is the only measure of substance this list has, there being
 * no status to say anything with.
 *
 * `created_at` is when the organisation was **tracked**, not when the entity was
 * minted — the two differ by however long an adopted node sat in the graph
 * unwatched, and what this list is about is the record. `updated_at` is the
 * entity's, which is what "last updated" means on every other list in this
 * system.
 *
 * There is no status, no latest run and no standing here. A status does not
 * exist for an organisation (R1); the run kind that would fill a "latest" column
 * is chunk 3's, and a column that could only ever say "never" is furniture.
 */
export const OrganisationSummary = z
  .object({
    id: Uuid,
    display_name: z.string(),
    connections: z.number().int().nonnegative(),
    created_at: Timestamp,
    updated_at: Timestamp,
    /**
     * **The protection account this organisation belongs to**, and `null` in
     * every vertical that has none (security fold, 2026-09-22). Required and
     * `.nullable()` rather than `.optional()`, which is this package's rule for
     * a field that can be "none".
     */
    account: RecordAccount.nullable(),
  })
  .strict();
export type OrganisationSummary = z.infer<typeof OrganisationSummary>;

/** The list's row — the same shape, under the name the list reads it by. */
export const OrganisationListEntry = OrganisationSummary;
export type OrganisationListEntry = z.infer<typeof OrganisationListEntry>;

/**
 * `GET /orgs/:orgId/organisations` — **tracked only**, and that is not a filter
 * (R8).
 *
 * The People list carries `level` because a known person is a row on it: a
 * spouse research minted is somebody the office may want to promote, so they are
 * findable there. An organisation the office does not track is findable too — in
 * the knowledge base, which is the every-kind surface and where Track is
 * offered. This list is the office's own roster, so there is nothing to widen it
 * to.
 *
 * `q` is the name search, matched the way every name search here is matched:
 * against the stored `normalized_name`, so punctuation and case in the query
 * cannot miss a record.
 */
export const ListOrganisationsQuery = z
  .object({
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
    /**
     * **Only the organisations on this protection account** (security fold,
     * 2026-09-22) — the same chip the People list carries, and refused with a
     * 400 in a vertical that has no accounts.
     */
    account_id: Uuid.optional(),
  })
  .strict();
export type ListOrganisationsQuery = z.infer<typeof ListOrganisationsQuery>;
export type ListOrganisationsQueryInput = z.input<typeof ListOrganisationsQuery>;

/**
 * The page. `next_cursor` is null on the last one — a keyset cursor carrying the
 * listing it was cut for, refused rather than reinterpreted when it is replayed
 * under a different search or page size, exactly as the People list's is.
 */
export const OrganisationListResponse = z
  .object({
    entries: z.array(OrganisationListEntry),
    next_cursor: z.string().nullable(),
  })
  .strict();
export type OrganisationListResponse = z.infer<typeof OrganisationListResponse>;

/* ————————————————————————— the record ————————————————————————— */

/**
 * **Who this organisation is to the office** — the head of its record page.
 *
 * It is the list entry plus the three things the office has *said* about the
 * organisation, and it says nothing else because there is nothing else: `kind`
 * and `tracked` are literals rather than fields, since a record that reached
 * this shape is a tracked organisation by construction and a reader who has to
 * check would be reading a union that does not exist.
 *
 * `website` is the **confirmed** `domain` anchor's host as the office typed it —
 * not the fold the resolver matches on, which is `medici org` and is not a
 * website. `location` and `jurisdiction` are the confirmed anchors of those two
 * kinds, which are the only other two the statements door writes. All three are
 * null until somebody states them, and a *candidate* one — a value research
 * proposed and nobody confirmed — is not this field: the record's head carries
 * what the office stands behind.
 *
 * **Three fields for the websites, because R10 says they are three different
 * things** (owner ruling, 2026-09-10): "Some companies may have multiple domains
 * (as aliases) anyway. The model just needs to be aware of it, and prioritize one
 * supplied by the office. Otherwise, it should use the domain where it actually
 * ran its searches."
 *
 *  - **`website`** is the primary and the one every reader that wants one site
 *    wants: a site the office supplied, earliest first; failing that, the site
 *    the last completed lookup actually read. It is `websites[0]`.
 *  - **`websites`** is what this company answers at — every confirmed site in the
 *    order the office confirmed them, then the site a run read where the office
 *    has not confirmed it. An organisation may have more than one, a *former* one
 *    included: Medici moved from `medici.md` to `medi.ci`, and the 2018 release
 *    printing the old address is still about this company.
 *  - **`aliases`** is what pages have merely printed — a press release's
 *    parenthesis, a directory's field. Kept, said under the record's own address
 *    as "Also seen as …", and never the primary: nobody has stood behind one, and
 *    a host a page named is a thing the record has met rather than a thing it is.
 *
 * A *candidate* anchor is not the `website` field and never was: the record's
 * head carries what the office stands behind, or — since R10 — what a run of this
 * record's own can say it read.
 */
export const OrganisationSubject = z
  .object({
    entity_id: Uuid,
    display_name: z.string(),
    kind: z.literal('org'),
    tracked: z.literal(true),
    connections: z.number().int().nonnegative(),
    /** When the office started keeping a record — the `subject_record` row. */
    created_at: Timestamp,
    updated_at: Timestamp,
    website: z.string().nullable(),
    /**
     * Every website this company answers at, primary first — the office's, then
     * the site the run read. Empty where nobody has typed one and no run has read
     * one.
     */
    websites: z.array(z.string()),
    /**
     * The hosts pages have merely printed, in the order the record met them.
     * Never the primary, and never a reason to disbelieve a page.
     */
    aliases: z.array(z.string()),
    location: z.string().nullable(),
    jurisdiction: z.string().nullable(),
  })
  .strict();
export type OrganisationSubject = z.infer<typeof OrganisationSubject>;

/**
 * **What the system says about the organisation**, from the current
 * `org_summary` analysis — and `null` until chunk 3's lookup writes one, which
 * is today and for every organisation.
 *
 * Two fields rather than the whole `AnalysisDto`: an organisation's summary has
 * no sensitivity to compute, no `as_of` the office reads, no inputs list and no
 * caveats block, because the kind that writes it is two to four sentences of
 * prose. When it grows any of those the field grows with it; carrying them empty
 * now would be a shape nobody could tell apart from one that had them.
 */
export const OrganisationSummaryText = z
  .object({
    text: z.string(),
    updated_at: Timestamp,
  })
  .strict();
export type OrganisationSummaryText = z.infer<typeof OrganisationSummaryText>;

/**
 * **Where one line of the profile was read**, in the words a reader can place.
 *
 * A profile is one lineage per organisation (the catalog's `org_profile` keys on
 * the subject alone), so a second source does not open a second block — it
 * corroborates this one. That makes "where did this come from" a *list*, and the
 * list is the only honest way to show it: a page that named one source for a
 * description three sources agree on would be picking one at random.
 *
 * `label` is the host the page was fetched from ("medici.org"), or the office's
 * own name for its own words. `url` is the page where there was one and null for
 * anything the office typed — a statement has no address.
 */
export const OrganisationProfileSource = z
  .object({
    label: z.string(),
    url: z.string().nullable(),
  })
  .strict();
export type OrganisationProfileSource = z.infer<typeof OrganisationProfileSource>;

/**
 * **What the organisation says about itself** — the current `org_profile` fact,
 * projected (catalog v6).
 *
 * It is the block that was missing on the day the first lookup ran: the site was
 * read, and the four types the leg could write had nowhere to put what a home
 * page actually says. The summary was then written out of the office's own
 * prose, which is the record telling itself back what it had already been told.
 *
 * **Distinct from {@link OrganisationSummaryText}, and the difference is who
 * wrote it.** The summary is a *model's* two-to-four sentences over everything
 * the run found; this is the *source's* own account, field by field, each value
 * as the page stated it. One is a reading and the other is a quote, and a page
 * that merged them would leave the office unable to tell which it was looking
 * at.
 *
 * **And distinct from the three anchors on {@link OrganisationSubject}.** Those
 * are what the *office* has confirmed and stands behind; these are what a source
 * said. `location`, `jurisdiction` and `website` deliberately appear in both
 * shapes for that reason — the head shows the office's word, this block shows
 * the page's, and where they disagree that is a thing worth seeing rather than a
 * duplication to collapse.
 *
 * `description` is the one field that is always there: a profile with no
 * sentence in it is a set of anchors, and the anchors have their own channel.
 */
export const OrganisationProfile = z
  .object({
    description: z.string(),
    location: z.string().nullable(),
    jurisdiction: z.string().nullable(),
    /** As the source wrote it — "1974", "March 2003" — never widened to a date. */
    founded: z.string().nullable(),
    /** The measure the source chose: "about 40 people", "$12m revenue". */
    size: z.string().nullable(),
    sector: z.string().nullable(),
    /** The host as stated, not the fold the resolver matches on. */
    website: z.string().nullable(),
    sources: z.array(OrganisationProfileSource),
  })
  .strict();
export type OrganisationProfile = z.infer<typeof OrganisationProfile>;

/**
 * **One thing the office wrote**, newest first — the "What the office says"
 * block, and the organisation's whole equivalent of a person's Notes rail.
 *
 * `extraction` is the prose row's own state under the name a reader can act on:
 * `read` is the extractor's `done`, and the other three are said as they are.
 * `failed` is the one that has to be on the wire at all — the words are saved
 * and the relationships in them were not read, and the page says so with a way
 * to ask again rather than leaving the office to wonder why a paragraph naming
 * three companies produced no relations.
 *
 * No author and no id. One organisation's record is the office's own, every
 * statement on it was written by somebody in this office, and the record page
 * has no per-statement door to address.
 */
export const OrganisationStatement = z
  .object({
    prose: z.string(),
    created_at: Timestamp,
    extraction: z.enum(['read', 'pending', 'failed', 'none']),
  })
  .strict();
export type OrganisationStatement = z.infer<typeof OrganisationStatement>;

/**
 * **Somebody the record says works there or sits on its board** — the Leadership
 * block, read off `employment` and `board_seat` facts with this organisation in
 * the counterparty column.
 *
 * Two fact types and one list, because the office's question is who runs this
 * company and the catalog's distinction between a job and a seat is not that
 * question. `role` is the word the source used where either type carried one
 * (`employment.role`, `board_seat.role`) and null where it did not — a person
 * named as being there without a title is still the answer.
 *
 * `tracked` is whether the office keeps a record of that person, which is what
 * decides whether the name is a link on the page. `since` is `employment.start`
 * or `board_seat.since` **exactly as the fact carries it** — a plain string, not
 * a `LooseDate`: the catalog's own type for those two fields is `dateish`, four
 * characters or more of whatever the source wrote, so a record that refused
 * "since 2019" would 500 on a fact the ingest was happy to write.
 *
 * `tier` is the seniority band the server ranks the list by, read from `role`:
 * founders and chief executives, other C-level officers and chairs, directors,
 * VPs, partners and board seats, everyone else, then assistants, advisers and
 * former holders. It is a sort heuristic on the title's words, not a verified
 * position. The list is one row per fact, so a person holding two roles appears
 * twice, each row with its own tier.
 */
export const LeaderTier = z.enum(['principal', 'executive', 'senior', 'staff', 'support']);
export type LeaderTier = z.infer<typeof LeaderTier>;

export const OrganisationLeader = z
  .object({
    person_entity_id: Uuid,
    display_name: z.string(),
    role: z.string().nullable(),
    /** Which fact says they are there; a seat with no stated title is still a seat. */
    fact_type: z.enum(['employment', 'board_seat']),
    tier: LeaderTier,
    tracked: z.boolean(),
    since: z.string().nullable(),
  })
  .strict();
export type OrganisationLeader = z.infer<typeof OrganisationLeader>;

/**
 * **Somebody who put money into it** — the Investors block, read off `investment`
 * facts with this organisation in the counterparty column (catalog v7, owner
 * ruling 2026-09-10: "noting investors in the business, when that data is
 * available. A separate section from leadership. Should create person nodes.").
 *
 * `leadership`'s shape with one field more and one fewer, and the differences are
 * the whole reason it is a second list rather than a second reading of the first.
 *
 * **`kind`, because an investor may not be a person.** A leader is a human being
 * by construction; a backer is as often a fund, a firm or a parent company, and a
 * list that could only hold people would either drop every fund or file one as a
 * person. It is what decides which page the name links to as much as what it is.
 *
 * **`round`, `amount`, `date` and `role` are the fact's own words**, each null
 * where the source did not state it. `amount` is a string for the catalog's
 * reason — "$24 million", "an undisclosed sum" — and `date` is the fact's
 * `dateish` exactly as it carries it, not a `LooseDate`, for `since`'s reason on
 * a leader: a record that refused what the ingest was happy to write would 500 on
 * its own data.
 *
 * `tracked` is whether the office keeps a record of that entity, which is what
 * decides whether the name is a link.
 */
export const OrganisationInvestor = z
  .object({
    entity_id: Uuid,
    display_name: z.string(),
    kind: KnowledgeKind,
    round: z.string().nullable(),
    amount: z.string().nullable(),
    date: z.string().nullable(),
    role: InvestmentRole.nullable(),
    tracked: z.boolean(),
  })
  .strict();
export type OrganisationInvestor = z.infer<typeof OrganisationInvestor>;

/**
 * **How this organisation stands to another**, on the record or not yet.
 *
 * One list and two origins, which is the point of the shape ("As grounded" 4).
 * `on_record: true` is an active `org_relation` fact with this organisation in
 * either column; `on_record: false` is a **held** `org_relation` claim — the
 * office named a company this org has never recorded, and the statements door
 * holds it rather than minting a node nobody verified. A record that showed only
 * the first would look as though the second had been thrown away.
 *
 * `other` is the record on the far side, and it is null exactly when
 * `on_record` is false: a held relation has no far side yet, which is what held
 * means. `name_as_written` is the other side's name **as the office wrote it**,
 * read off the claim's own payload — the held reference never resolved, so
 * `claim_entity_resolution` has no row for it and the words are the only account
 * there is. It is null on a relation that is on record, where the far side has a
 * name of its own.
 *
 * `sentence` is the catalog's own `orgRelationSentence` over the pair, so the
 * line here is the line the fact carries and the line the merge card promised.
 * A held one reads "Not yet on record: Acme Holdings" instead: there are not two
 * names to make a sentence out of, and inventing one from a name nobody has
 * matched would state a relationship between this record and a string.
 */
export const OrganisationRelation = z
  .object({
    relation: OrgRelation,
    sentence: z.string(),
    other: z
      .object({ entity_id: Uuid, display_name: z.string(), kind: KnowledgeKind })
      .strict()
      .nullable(),
    on_record: z.boolean(),
    name_as_written: z.string().nullable(),
  })
  .strict();
export type OrganisationRelation = z.infer<typeof OrganisationRelation>;

/**
 * `GET /orgs/:orgId/organisations/:id` — **the organisation's record**, and what
 * the create answers with.
 *
 * The person record's shape with everything an organisation does not have taken
 * out, and two blocks that are its own. What is shared is shared by reuse rather
 * than by resemblance: `runs` is {@link RunSummary}, `provenance` is the
 * knowledge panel's {@link ProvenanceEntry}, and `pending_decisions` is the
 * record's own {@link DecisionSummary}, so a client renders each of the three
 * with the component it already has.
 *
 * **`pending_decisions` is empty for every organisation today**, and it is here
 * anyway. It costs one indexed query, and the row that will fill it is already
 * specified: chunk 3's lookup proposes a rename ("This looks like it is really
 * called Pogue Holdings"), which is a pending `entity_rename` keyed on this
 * subject. A field added later would be a second deploy for the same page.
 *
 * **`profile` is a fact, said in its own words** (catalog v6). It is the current
 * `org_profile` — what a source said the organisation does, where it sits, how
 * big it is — and it is broken out for the same reason `leadership` and
 * `relations` are: those three *are* an organisation's facts as the office reads
 * them, and a generic fact row rendering "description: …" would be the record
 * making the office do the projection.
 *
 * There is no `facts` block and no `held_claims` block. An organisation's facts
 * that the office reads are its leadership, its investors and its relations,
 * which are here under names that say what they are; the rest of what the graph holds about it
 * is the knowledge panel's, which is the every-kind surface and already draws
 * them.
 */
export const OrganisationRecord = z
  .object({
    subject: OrganisationSubject,
    summary: OrganisationSummaryText.nullable(),
    /** The current `org_profile` fact, or null until a source states one. */
    profile: OrganisationProfile.nullable(),
    statements: z.array(OrganisationStatement),
    leadership: z.array(OrganisationLeader),
    /** Who put money in, newest round first. Empty until a source states one. */
    investors: z.array(OrganisationInvestor),
    relations: z.array(OrganisationRelation),
    runs: z.array(RunSummary),
    provenance: z.array(ProvenanceEntry),
    pending_decisions: z.array(DecisionSummary),
  })
  .strict();
export type OrganisationRecord = z.infer<typeof OrganisationRecord>;

/**
 * What `POST /orgs/:orgId/organisations` answers with — **the seeded record**,
 * exactly as the person create answers with the record it just made.
 *
 * Aliased rather than re-declared so there is one place it changes, and so the
 * door and the page are provably the same shape: an office that has just entered
 * an organisation lands on its record with nothing left to fetch.
 */
export const CreateOrganisationResponse = OrganisationRecord;
export type CreateOrganisationResponse = z.infer<typeof CreateOrganisationResponse>;

/* ————————————————————————— the statement ————————————————————————— */

/**
 * `POST /orgs/:orgId/organisations/:id/statements` — **Add what you know**
 * (R5's third door), and the same code path the create door's `website` and
 * `prose` take.
 *
 * One door, two fields, and **at least one of them**: a statement that states
 * nothing is a press that would file an empty decision and spend an extraction
 * on an empty string. Both are nullable because the office may have only one of
 * the two, and `.strict()` because a third field here would be a fourth effect
 * nobody asked for.
 *
 * `website` is not `z.string().url()`. A URL check would refuse `medici.org`,
 * which is what somebody actually types, and accept `https://example`, which
 * names no host — so the reading is the server's (`identity/website-host.ts`)
 * and what comes back for an unreadable one is `WEBSITE_UNREADABLE` with a
 * sentence, not a field error about a protocol.
 */
export const OrganisationStatementBody = z
  .object({
    /** The organisation's own site. Its host becomes a confirmed `domain` anchor. */
    website: z.string().trim().nullable(),
    /**
     * What the office knows, in their own words. Filed, and read for relations.
     *
     * Bounded by {@link PROSE_STATEMENT_MAX_LENGTH} for the reason the create's
     * is: this paragraph is read inside this request and is never split. The
     * `refine` below still owns emptiness — a statement saying nothing at all is
     * refused there, with the sentence the office reads.
     */
    prose: z.string().trim().max(PROSE_STATEMENT_MAX_LENGTH).nullable(),
  })
  .strict()
  .refine((body) => Boolean(body.website?.trim()) || Boolean(body.prose?.trim()), {
    message: 'Say something: a website, what you know, or both.',
    path: ['prose'],
  });
export type OrganisationStatementBody = z.infer<typeof OrganisationStatementBody>;

/**
 * What the statement did, in counts and in one sentence.
 *
 * **No ids and no lists.** The office reads the sentence; the record page reads
 * itself again afterwards and shows the relations, held ones included, in the
 * place they belong. A payload naming the organisations here would be the same
 * information in a second shape that could disagree with the first.
 *
 * `relations_held` is the count that matters and the reason it is on the wire at
 * all: an organisation the statement named and this org has never heard of is
 * **held**, never minted ("As grounded" 4), and a door that silently recorded
 * fewer relations than the sentence described would look like it lost them.
 *
 * `website_confirmed` is true when the record's confirmed website is now the one
 * the office sent — including the press that sent the same one twice, which
 * writes nothing, and the press that named a **second** site, which confirms it
 * beside the first (2026-09-10). It says the site is on the record, never that
 * it is the only one.
 */
export const OrganisationStatementResponse = z
  .object({
    website_confirmed: z.boolean(),
    relations_recorded: z.number().int().nonnegative(),
    relations_held: z.number().int().nonnegative(),
    sentence: z.string(),
  })
  .strict();
export type OrganisationStatementResponse = z.infer<typeof OrganisationStatementResponse>;

/**
 * `WEBSITE_UNREADABLE` details — the refusal as a sentence, in the plain-string
 * list `NameTakenDetails` uses so one client reader covers both.
 *
 * It used to cover a second code, `WEBSITE_ALREADY_CONFIRMED`, which refused a
 * website because the record already had a different one. That refusal is gone
 * (owner ruling, 2026-09-10: an organisation may have more than one confirmed
 * website, a former one included), so one refusal is left — the value that names
 * no host at all.
 */
export const WebsiteRefusalDetails = z
  .object({
    issues: z.array(z.string()),
  })
  .strict();
export type WebsiteRefusalDetails = z.infer<typeof WebsiteRefusalDetails>;

/** The sentence `WEBSITE_UNREADABLE` carries. No field path, no protocol talk. */
export const websiteUnreadableSentence = (): string => 'That does not look like a website address.';

/**
 * **What the office reads when a second website goes on the record** (owner
 * ruling, 2026-09-10) — where the first one earns "Recorded the website."
 *
 * It names the host, and it has to: a press that adds `medici.md` to a record
 * whose head says `medi.ci` has not changed the site the office sees anywhere,
 * and a sentence that only said "Recorded the website" would read as though it
 * had. "Another" rather than "a former": the office may be recording a second
 * live address, and nothing on this door knows which.
 */
export const anotherWebsiteSentence = (host: string): string =>
  `Recorded ${host} as another website of this organization.`;

/* ————————————————————————— untrack ————————————————————————— */

/**
 * `DELETE /orgs/:orgId/organisations/:id` — **Stop tracking** (R8).
 *
 * It drops the `subject_record` row and **nothing else**. This is not the
 * person shred: nothing is tombstoned, no claim, fact, analysis or run is
 * touched, and the organisation stays exactly where it was in the knowledge
 * base, on the graph and on everybody's record as a counterparty. The office is
 * saying they no longer keep a record of it, which is a smaller statement than
 * "this never happened" and is the only one an organisation has a door for.
 *
 * The answer names the organisation rather than repeating its id, because what
 * the office reads is a sentence.
 */
export const UntrackOrganisationResponse = z
  .object({
    untracked: z.literal(true),
    display_name: z.string(),
  })
  .strict();
export type UntrackOrganisationResponse = z.infer<typeof UntrackOrganisationResponse>;
